import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { pourcentageDeRemplissage } from '@groupe-trousseau/shared/lib';
import { PanneauDeVisio } from '../features/cours/PanneauDeVisio';
import { useCanalDuCours } from '../features/cours/canal';
import { useDocumentDuTableau } from '../features/cours/document';
import { useFenetreDInactivite } from '../features/cours/inactivite';
import { useDepotDImages } from '../features/cours/images';
import {
  SurfaceDeTrace,
  type CommandesDuTableau,
  type CurseurNomme,
  type ElementDeTableau,
  type Encre,
  type ImageDeTableau,
  type Outil,
  type PenseBeteDeTableau,
} from '../features/cours/moteur';
import {
  useCirculationEntreTableaux,
  useEcrire,
  useEntrerDansLeCours,
  useMettreAJourSurUnTableau,
  useOuvrirUnTableau,
  useRepondreALaFenetre,
  useRetirerDuTableau,
  useScene,
  useSortirDuCours,
  useTableauCourant,
  useTerminerLeCours,
  useAjouterUnParticipant,
} from '../features/cours/tableau';
import { useAnnuaire } from '../features/conversations/conversations';
import { useEspace, useOuvrirUnFichier } from '../features/espace/espace';
import {
  SEUIL_DE_MESSAGE_S,
  useCouperLeFlux,
  useVisioDuCours,
} from '../features/cours/visio';
import {
  usePoserUnePreference,
  useReglages,
} from '../features/reglages/reglages';
import { useSession } from '../features/session/session';
import { lireLErreur } from '../shared/api/client';
import { nomAffiche } from '../shared/format/designation';
import { Bandeau } from '../shared/ui/Bandeau';
import { BarreDOutils } from '../shared/ui/BarreDOutils';
import { Bouton } from '../shared/ui/Bouton';
import { Saisie } from '../shared/ui/Champ';
import { ChoixDeFichiers } from '../shared/ui/ChoixDeFichiers';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { Jauge } from '../shared/ui/Jauge';
import { Selection } from '../shared/ui/Selection';
import { Squelette } from '../shared/ui/Squelette';
import { Titre } from '../shared/ui/Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE TABLEAU DU COURS — LA SURFACE POUR LAQUELLE LE PRODUIT EXISTE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CDC-2` F-21 à F-30, et `DESIGN.md` en donne la densité : « **minimale, presque
 * nulle** — tout ce qui n'est pas le tracé prend de la place sur le tracé, et **la latence
 * du trait est le critère numéro un** ». Le fait qui décide tout, écrit au brief : « le
 * tuteur et l'élève passent **le cours entier dans un seul écran**, le tableau en plein
 * écran. **Ils n'y naviguent pas, ils y travaillent.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CINQ RÈGLES DE CET ÉCRAN NE SE DEVINENT PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **1. AU PLAFOND, TOUT LE MONDE EST GELÉ** (F-21) : « le refus porte sur **le tableau**,
 * pas sur la personne. Le cours est gelé en écriture, **pas même le tuteur ne trace**. »
 * Et « le produit propose d'ouvrir un tableau neuf, et **n'importe quel présent
 * l'ouvre** — le premier qui accepte débloque le cours pour tout le monde ». **C'est le
 * premier geste qu'un élève exerce sur la structure d'un cours** : le bouton n'a donc
 * aucune condition de rôle, et il n'y a nulle part où en poser une.
 *
 * **2. LE PRODUIT NE MONTRE PAS LA QUALITÉ DE LIAISON DES AUTRES** (DD-07) : « aucun voyant
 * sur qui que ce soit ». Cet écran n'affiche donc **sa propre** coupure, et rien de celle
 * des autres — « l'asymétrie est voulue : **sur sa propre coupure une personne peut
 * agir** » (F-30).
 *
 * **3. PENDANT SA COUPURE, ON CONTINUE DE TRACER**, et le produit prévient que ce qui est
 * tracé peut ne pas être retenu (Q405). Le canevas n'est donc **pas** bloqué à la
 * déconnexion — le bloquer déciderait à la place de la personne, et « en deçà de trente
 * secondes, rien ne se passe » de toute façon.
 *
 * **4. AUCUN ÉTAT ANTÉRIEUR DU TABLEAU N'EST GARDÉ** (Q403) : « rien ne se ramène en
 * arrière. Ce qui existe à la place est **la circulation entre les tableaux du cours**. »
 *
 * **5. LE MESSAGE DE FIN RAMÈNE À L'ÉCRAN D'ARRIVÉE** (F-54), et il est **le même quel que
 * soit le motif** : « une coupure par un administrateur ne se distingue de rien ».
 */
export function PageCours() {
  const { coursId } = useParams<{ coursId: string }>();
  const navigate = useNavigate();
  const session = useSession();

  const [outil, poserOutil] = useState<Outil>('crayon');
  const [encre, poserEncre] = useState<Encre>('noir');
  const [epaisseur, poserEpaisseur] = useState(3);
  const [poidsLocal, poserPoidsLocal] = useState(0);
  /**
   * **Les commandes du moteur, remontées une fois qu'il est monté.**
   *
   * D-012 impose que le moteur soit derrière une interface étroite : ce sont **des
   * fonctions**, pas son API, et c'est ce qui permet à cette page de déclencher annuler,
   * refaire, tout effacer et l'export **sans jamais nommer la bibliothèque**.
   */
  const [commandes, poserLesCommandes] = useState<CommandesDuTableau | null>(null);


  const entree = useEntrerDansLeCours(coursId ?? '');
  const tableau = useOuvrirUnTableau(coursId ?? '');
  const canal = useCanalDuCours(coursId ?? null);
  const fin = useTerminerLeCours(coursId ?? '');
  const sortie = useSortirDuCours(coursId ?? '');
  const ajout = useAjouterUnParticipant(coursId ?? '');
  const [ajoutOuvert, poserLAjoutOuvert] = useState(false);
  const [eleveChoisi, poserLEleveChoisi] = useState('');
  /** F-24 — les deux chemins d'entrée qui demandent qu'on désigne quelque chose. */
  const [priseOuverte, poserLaPriseOuverte] = useState(false);
  const [adresseCollee, poserLAdresseCollee] = useState('');

  /**
   * **La source des élèves ajoutables est l'annuaire, et c'est la page qui compose les
   * deux features** — ce que `pages/` est, et ce que la barrière de `DOCTRINE.md` §10
   * impose depuis D-113 : « une feature n'atteint QUE la sienne ».
   *
   * L'annuaire rend « vos relations actives », donc **pour un tuteur, ses élèves** —
   * exactement la garde de F-16, « refusé si la relation n'existe pas », **sans que cet
   * écran ait à la vérifier** : ce qu'il ne propose pas ne peut pas être refusé pour ce
   * motif. La seconde garde, le plafond d'élèves, reste au serveur et se lit au refus.
   *
   * **Il n'est lu que quand le geste s'ouvre** : c'est une requête de plus sur l'écran
   * dont « la latence du trait est le critère numéro un », et le geste est rare.
   */
  const annuaire = useAnnuaire(ajoutOuvert);

  /**
   * **LA PRÉFÉRENCE DE MICRO ET DE CAMÉRA EST COMPOSÉE ICI, ET C'EST UNE BARRIÈRE DE
   * LINT QUI L'A DÉCIDÉ.**
   *
   * Elle vit dans les réglages, F-31 l'applique à l'entrée dans un cours, et « couper ou
   * rallumer pendant un cours met à jour cette préférence » (Q419, Q420). La feature de
   * la visio ne peut pas aller la chercher : `DOCTRINE.md` §10, encodé dans
   * `eslint.config.mjs`, pose qu'« une feature n'atteint QUE la sienne ». **Assembler
   * deux features est exactement ce que `pages/` est**, et rien de ce qui suit n'est de
   * la logique métier — deux valeurs et un renvoi.
   *
   * **Le repli est Q563 et non une prudence** : « à la toute première entrée, le micro
   * est allumé ET la caméra est allumée ». C'est aussi la valeur par défaut du modèle.
   */
  const reglages = useReglages();
  const poserUnePreference = usePoserUnePreference();
  const preferenceDeMedia =
    reglages.data?.etendue === 'complete'
      ? reglages.data.preferences.media
      : null;
  const preference = useMemo(
    () => ({
      micro: preferenceDeMedia?.micro ?? true,
      camera: preferenceDeMedia?.camera ?? true,
      onBascule: (flux: 'micro' | 'camera', ouvert: boolean) =>
        poserUnePreference.mutate({ nom: 'media', valeur: { [flux]: ouvert } }),
    }),
    [preferenceDeMedia?.micro, preferenceDeMedia?.camera, poserUnePreference],
  );

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LA VISIO N'ENTRE QU'APRÈS L'ENTRÉE DANS LE COURS — F-31, et c'est la
   * même leçon que D-097.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * « **Entrer dans un cours, c'est entrer dans son canal. Aucun geste séparé, aucun
   * écran d'attente** » (DD-15) : il n'y a donc pas de bouton pour rejoindre la visio,
   * elle se monte avec l'écran. **Mais le droit d'entrée n'est émis qu'à une personne
   * PRÉSENTE** — mesuré à la main le 20260827 : sans l'entrée, la route répond
   * `403 PAS_PRESENT_DANS_LE_COURS`. Demander le droit avant que l'entrée ait réussi
   * refaisait exactement le défaut de D-097, où les deux requêtes du chargement
   * partaient sur une personne que le serveur ne voyait pas dans le cours.
   */
  const visio = useVisioDuCours(coursId ?? null, entree.isSuccess, preference);
  const coupure = useCouperLeFlux(coursId ?? '');

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LE TABLEAU OÙ ON ÉCRIT EST CELUI QUE LE SERVEUR DÉSIGNE — D-110.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Cette page en OUVRAIT un à chaque montage**, sur la foi du commentaire ci-dessous,
   * qui affirmait que « `ouvrirUnTableau` rend le tableau courant ». **C'était faux** :
   * `boards.service.ts` fait `db.board.create` **sans condition**, et « l'ouverture du
   * tableau suivant FIGE le précédent ».
   *
   * **Trois conséquences, toutes constatées au navigateur à deux postes.** Le second
   * arrivant figeait le tableau du premier, dont les écritures repartaient en
   * `409 TABLEAU_GELE` — **le trait s'affichait quand même**, posé localement, et **rien
   * n'était conservé**. Il arrivait sur un tableau **vide** alors que le cours en portait
   * un garni. Et chaque arrivée ou rechargement consommait un rang : au cinquième,
   * `maxBoardsPerCourse` par défaut, **le cours entier était gelé** — « le tableau est
   * surchargé » après quelques dessins.
   *
   * **La bonne source existait déjà et ce code la jetait** : le salon du canal porte
   * `tableauCourantId` depuis le LOT-4a. `useScene`, écrit pour « le tableau vivant s'y
   * recharge », **n'avait aucun appelant**.
   */
  /**
   * **LA LECTURE DU TABLEAU VIVANT, ET ELLE FERME LE TROU DE LA JAUGE — D-110.**
   *
   * Elle n'existait pas quand D-110 a corrigé le défaut 3 : « aucune route ne rend l'état
   * du tableau courant **sans en créer un** », donc la jauge partait de zéro jusqu'au
   * premier trait. Elle est la **première** source du tableau vivant depuis le 20260828,
   * et le salon reste le repli : entre une valeur relue à l'arrivée et une valeur figée au
   * handshake, on préfère la première.
   *
   * **Elle attend que l'entrée ait réussi**, comme tout le reste de cet écran : le serveur
   * réserve ce geste aux présents, et D-097 a montré ce que coûte une page qui demande
   * avant d'être entrée — « les deux seules requêtes du chargement sont refusées ».
   */
  const tableauCourant = useTableauCourant(coursId ?? '', entree.isSuccess);

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **F-18 — LA FENÊTRE D'INACTIVITÉ, ET CE QUI L'A RENDUE MONTRABLE.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Ce geste a été arrêté le 20260828 plutôt que câblé** — D-118 : « rien ne dit à une
   * surface que la fenêtre est ouverte, et il n'y a donc **rien à quoi répondre** ». Ce
   * qui a changé n'est pas la route, c'est **l'annonce** : le canal porte l'activité du
   * cours (D-120), donc cet écran sait quand la fenêtre est posée — et il le sait **au
   * même instant que les autres postes**, ce que Q290 exige.
   */
  const fenetre = useFenetreDInactivite(canal.etat.activite);
  const reponseALaFenetre = useRepondreALaFenetre(coursId ?? '');

  const tableauVivantId =
    tableau.data?.id ?? tableauCourant.data?.id ?? canal.etat.tableauCourantId;
  const ecriture = useEcrire(tableauVivantId ?? '');
  const retrait = useRetirerDuTableau(tableauVivantId ?? '');
  /**
   * **F-22 — un élément déplacé ou redimensionné se conserve, depuis le 20260829.**
   *
   * D-121 : « déplacer ou redimensionner un élément déjà annoncé **ne se conserve
   * pas**, il revient au rechargement là où il est né », parce que « la route
   * d'écriture **crée**, et aucune route ne met à jour ». La route existe, et le moteur
   * appelle ce hook au lieu de sauter l'élément.
   */
  const miseAJour = useMettreAJourSurUnTableau(tableauVivantId ?? '');

  /**
   * **F-25 — le document fabriqué depuis le tableau, et il est ouvert à tout le monde.**
   *
   * **Aucune condition de rôle**, et c'est la règle : « le droit de fabriquer suit le
   * droit de relire, **sans ligne à lui** » (Q370), ce qui « ferme un item que le cadrage
   * laissait ouvert : **l'élève aussi** ». Un test de rôle ici retirerait à l'élève un
   * geste que le serveur lui accorde.
   *
   * **Ce que la personne obtient dépend quand même de son rôle**, et c'est le serveur qui
   * le dit et non cet écran : un `tutor` ou un `student` a un espace de fichiers, donc le
   * document y est rangé en plus d'être téléchargé ; un `orgadmin` « le reçoit en
   * téléchargement seul, et son droit de fabriquer ne disparaît pas » (Q450).
   */


  /**
   * **La scène en place, chargée quand on n'a pas ouvert le tableau soi-même.** « La scène
   * est rechargée entière à chaque retour dans le cours » (F-21). Un tableau qu'on vient
   * d'ouvrir est vide : le relire serait un aller-retour pour rien.
   */
  /**
   * **La scène en place, et le champ `cree` décide si on la charge — D-110.**
   *
   * Un tableau qu'on vient d'**ouvrir** est vide : le relire serait un aller-retour pour
   * rien. Mais depuis que la route est un « rends-le ou crée-le », elle peut rendre **un
   * tableau garni** — c'est le cas de la course qu'elle ferme, deux postes qui arrivent
   * ensemble. Supposer « ce que j'ai demandé est neuf » ouvrirait alors un écran vide sur
   * un tableau qui ne l'est pas, ce qui est exactement le défaut 3 de D-110 remis en place
   * par l'autre bout.
   */
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **F-26 ET F-47 — CIRCULER ENTRE LES TABLEAUX DU COURS, PENDANT ET APRÈS.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * « **On circule librement entre les tableaux d'un cours, pendant et après**, les
   * précédents en lecture seule » (Q378). « **La surface montre le rang du tableau
   * courant et le nombre de tableaux du cours**, et on circule de l'un à l'autre »
   * (F-47). **Aucune surface ne le permettait**, et c'est la remontée de D-121.
   *
   * **Le rang VIVANT reste celui que le serveur désigne** — trois sources, dans l'ordre
   * de fraîcheur : le tableau qu'on vient d'ouvrir, la lecture du tableau courant, le
   * salon du canal.
   *
   * **La circulation est dans la feature, pas ici** : chaque visite relit la scène au
   * serveur, parce que la lecture **est** le geste — elle laisse la ligne de trace que
   * DD-12 exige « quel que soit le lecteur », et elle repousse la fenêtre d'inactivité
   * « pour tout le cours » (F-18 critère 3), le serveur le faisant **dans**
   * `relireUneScene`. Le motif complet est au hook.
   */
  const rangVivant =
    tableau.data?.rang ??
    tableauCourant.data?.rang ??
    canal.etat.tableauCourantRang;
  const circulation = useCirculationEntreTableaux(coursId ?? '', rangVivant);

  /**
   * **Un tableau qu'on vient d'OUVRIR est vide : le relire serait un aller-retour pour
   * rien** — mais depuis que la route est un « rends-le ou crée-le », elle peut rendre
   * **un tableau garni**, et supposer « ce que j'ai demandé est neuf » ouvrirait un
   * écran vide sur un tableau qui ne l'est pas (D-110, défaut 3 par l'autre bout).
   *
   * **La relecture, elle, se charge toujours** : c'est exactement ce qu'on est allé
   * chercher.
   */
  const rangALire =
    circulation.enRelecture || tableau.data === undefined || !tableau.data.cree
      ? circulation.rang
      : null;
  const scene = useScene(coursId ?? '', rangALire ?? 0, rangALire !== null);

  /**
   * **Le nombre de tableaux du cours — F-47, « et on circule de l'un à l'autre ».**
   *
   * Deux sources, et la scène est la plus complète : elle compte **les tableaux
   * effacés**, « leur ligne survit parce qu'elle porte le rang, et sauter un numéro
   * donnerait à croire qu'un tableau a disparu de l'histoire du cours ». La lecture du
   * tableau courant le rend aussi, et sert quand aucune scène n'est chargée.
   */
  const nombreDeTableaux =
    scene.data?.nombreDeTableaux ?? tableauCourant.data?.nombreDeTableaux ?? null;

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **LE DOCUMENT S'ADRESSE AU TABLEAU QU'ON REGARDE, ET C'ÉTAIT UN DÉFAUT.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Le bouton était lié au tableau **vivant**. Dès qu'on circule vers un tableau
   * précédent, il aurait donc fabriqué le document **d'un autre tableau que celui
   * affiché à l'écran** — le nom de fichier, l'autorisation et les scènes rendues
   * auraient tous porté sur une scène que personne ne regarde.
   *
   * **La scène rend son `tableauId` depuis le 20260829**, ce qui n'était pas le cas :
   * `PageRelecture` ne pouvait pas fabriquer le document pour cette raison exacte,
   * constatée au commit `ca45270`. Le tableau vivant reste le repli, pour l'instant qui
   * précède le chargement de la scène.
   */
  const tableauAffiche = scene.data?.tableauId ?? tableauVivantId;
  const documentDuTableau = useDocumentDuTableau(tableauAffiche ?? '');

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **ON ENTRE DANS LE COURS AVANT D'OUVRIR SON TABLEAU, ET L'ORDRE EST LE
   * CORRECTIF — D-097.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **Cette page n'entrait pas.** `useEntrerDansLeCours` existait dans la feature depuis
   * le LOT-2 et **aucune page ne l'appelait** — l'un des deux seuls hooks de `web/` dans ce
   * cas. Elle ouvrait donc directement un tableau, et le serveur refusait :
   * **`403 PAS_PRESENT_DANS_LE_COURS`**, « ce geste appartient à une personne présente dans
   * le cours ».
   *
   * **LE TABLEAU ÉTAIT DONC INUTILISABLE, ET RIEN NE LE DISAIT.** Constaté au navigateur le
   * 20260826 : la page se monte, la barre d'outils paraît, la jauge s'affiche — et **les
   * deux seules requêtes du chargement sont refusées**. Un écran qui a l'air de marcher et
   * dont aucun geste ne passe.
   *
   * **CE QUE LA PRÉSENCE EST, ET POURQUOI ELLE N'EST PAS UN DÉTAIL D'ORDRE.** Entrer dans
   * un cours **ouvre une ligne du relevé des entrées et des sorties**, qui est « la source
   * de vérité de toutes les durées du produit ». Ce n'est donc pas une formalité avant le
   * tableau : c'est **le geste qui fait que le temps de cours se mesure**. Une page qui
   * ouvrait un tableau sans entrer aurait, si le serveur l'avait laissée faire, produit un
   * cours travaillé et **une durée nulle**.
   *
   * **`useEffect` et non `useMemo`**, et c'est une correction au passage : `useMemo` sert à
   * mémoriser une valeur, pas à déclencher un effet. React ne garantit pas qu'il ne le
   * rejoue pas, et il s'exécute **pendant le rendu** — poser une mutation là est ce que la
   * bibliothèque interdit. Les deux gardes `isIdle` empêchent le second appel.
   */
  useEffect(() => {
    if (coursId !== undefined && entree.isIdle) entree.mutate();
  }, [coursId, entree]);

  /**
   * **ON N'OUVRE UN TABLEAU QUE S'IL N'EN EXISTE AUCUN — corrigé le 20260826, D-110.**
   *
   * **Trois conditions, et aucune n'est de trop.** L'entrée doit avoir réussi, sans quoi le
   * serveur refuse le geste à qui n'est pas présent — c'est le correctif de D-097. Le salon
   * doit avoir répondu : **avant, on ne sait pas s'il existe un tableau**, et ouvrir « au
   * cas où » est exactement le défaut qu'on corrige. Et le salon doit dire qu'il n'y en a
   * aucun.
   *
   * **Il reste une course, et elle est nommée plutôt que cachée** : deux postes qui entrent
   * ensemble sur un cours neuf voient tous deux `null` et ouvrent chacun un tableau. Le
   * second fige le premier. Elle est **bornée à la première arrivée simultanée d'un cours
   * neuf**, là où le défaut d'avant se reproduisait à **chaque** arrivée et à chaque
   * rechargement. La fermer demande un « rends-le ou crée-le » côté serveur, donc un
   * changement de sémantique de route : **remonté, pas décidé ici.**
   */
  useEffect(() => {
    if (
      coursId !== undefined &&
      entree.isSuccess &&
      tableauCourant.isSuccess &&
      tableauCourant.data.id === null &&
      tableau.isIdle
    ) {
      tableau.mutate();
    }
  }, [coursId, entree.isSuccess, tableauCourant.isSuccess, tableauCourant.data, tableau]);

  /**
   * **La jauge lit la dernière écriture, puis l'ouverture, dans cet ordre — D-110.**
   *
   * Elle ne lisait que l'ouverture, ce qui marchait parce qu'on en faisait une à chaque
   * montage. **Aucune route ne rend le poids du tableau courant sans en créer un** : sur un
   * tableau qu'on rejoint, la jauge part donc de zéro et **se corrige à la première
   * écriture**, `ecrireSurUnTableau` rendant `poidsCourant` et `plafondOctets`.
   *
   * **Ce que ça coûte est écrit** : entre l'arrivée et le premier trait, la jauge d'un
   * tableau presque plein annonce zéro. Le refus, lui, reste juste — il vient du serveur, et
   * l'écran le dit déjà. Combler ce trou demande une lecture du tableau courant que le
   * contrat n'a pas : **remonté avec D-110.**
   */
  /**
   * **ET UN RETRAIT EST UNE SOURCE DE POIDS AUTANT QU'UNE ÉCRITURE — ajouté le
   * 20260828 avec la gomme.**
   *
   * « Ce qui est retiré ne compte plus dans le poids, et l'ajout redevient possible »
   * (Q326) : la route de retrait rend le même état de remplissage que celle d'écriture.
   * Ne lire que l'écriture ferait **monter la jauge et jamais descendre** — on efface un
   * tableau presque plein, et il reste annoncé presque plein jusqu'au trait suivant.
   *
   * **La plus fraîche des deux gagne**, par l'instant d'envoi, parce que les deux
   * mutations vivent côte à côte et qu'aucune n'est postérieure à l'autre par nature.
   */
  const dernierEtat =
    retrait.data !== undefined &&
    (ecriture.data === undefined || retrait.submittedAt > ecriture.submittedAt)
      ? retrait.data
      : ecriture.data;
  const plafondConnu =
    dernierEtat?.plafondOctets ??
    tableau.data?.plafondOctets ??
    tableauCourant.data?.plafondOctets ??
    null;
  const poidsServeur =
    dernierEtat?.poidsCourant ??
    tableau.data?.poidsCourant ??
    tableauCourant.data?.poidsCourant ??
    0;
  // **La règle vient de `shared/lib`, et les deux côtés la rejouent.** Le serveur rend
  // son pourcentage à chaque écriture ; entre deux, c'est celui-ci qui tient la jauge
  // « visible de tous pendant tout le cours ».
  /**
   * **UN PLAFOND INCONNU N'EST PAS UN PLAFOND ATTEINT — et le confondre gelait tout.**
   *
   * Le repli valait `0`, et `pourcentageDeRemplissage(x, 0)` rend cent : **la surface
   * s'ouvrait « Ce tableau est plein » sur un tableau vide**, pour les deux postes, et
   * personne ne pouvait tracer. Constaté au navigateur en corrigeant D-110 — la valeur ne
   * venait plus de l'ouverture d'un tableau, puisqu'on n'en ouvre plus.
   *
   * **Le sens sûr est l'inverse** : tant qu'on ne connaît pas le plafond, on ne gèle rien.
   * Si le tableau est réellement plein, **le serveur refuse** et l'écran le dit déjà — le
   * refus est juste, il vient de l'autorité. Geler à tort ferme un cours qui va bien ;
   * ne pas geler à temps coûte un refus expliqué.
   */
  const pourcentage =
    plafondConnu === null
      ? null
      : pourcentageDeRemplissage(
          Number(poidsServeur) + poidsLocal,
          Number(plafondConnu),
        );
  const gele = pourcentage !== null && pourcentage >= 100;

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **F-24 — LE DÉPÔT D'UNE IMAGE, ET IL EST FERMÉ EN RELECTURE ET AU PLAFOND.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Deux raisons différentes, et aucune n'est l'autre : une scène passée « est figée,
   * personne ne l'écrit » (Q317), et au plafond « tout ajout est refusé pour **tous** les
   * présents — pas même le tuteur ne trace » (F-21). Le serveur refuserait de toute
   * façon ; ne pas montrer le geste évite un bouton qui mène à un refus, ce que D-111 a
   * mesuré.
   *
   * **Les sept chemins, et les trois qui ne sont pas servis, sont écrits au module** —
   * pas ici : une page n'explique pas une règle qu'elle ne porte pas.
   */
  const depot = useDepotDImages({
    tableauId: tableauVivantId ?? null,
    commandes,
    actif: !gele && !circulation.enRelecture,
  });

  /**
   * **La scène en place, telle que le serveur la rend.**
   *
   * **Ce que ce poste a posé lui-même n'y figure PAS, et c'est le renversement de
   * D-115** : le moteur est l'autorité locale sur ce qui est à l'écran. Avant, cette page
   * tenait une liste `poses` de ce qu'elle venait de dessiner et la repassait au canevas,
   * qui la repeignait ; **avec un moteur, lui rendre ses propres éléments les écraserait
   * sous le stylet**. Il garde ce qu'il a, il reçoit ce qui vient d'ailleurs.
   *
   * `auteurPrenom` est ignoré ici : « la relecture n'expose pas d'identifiant » (F-47), et
   * l'auteur d'un tracé « est une trace, pas un droit » (Q353) — rien à l'écran ne s'en
   * sert, et le moteur n'a pas de champ pour le porter.
   */
  const elementsEnPlace: readonly ElementDeTableau[] = useMemo(
    () =>
      (scene.data?.traces ?? []).map(
        (t) => t.contenu as ElementDeTableau,
      ),
    [scene.data],
  );

  /**
   * **Les images de la scène — F-24, et rien ne les peignait.**
   *
   * D-116 : « **le canevas ne peint que des tracés** — `ElementPose` ne connaît qu'une
   * `Trace`, et **`scene.data.images` n'est lu par personne** ». Le port les peint
   * désormais, depuis leur adresse signée : il faut donc les lui passer.
   */
  const imagesEnPlace: readonly ImageDeTableau[] = useMemo(
    () =>
      (scene.data?.images ?? []).map((image) => ({
        id: image.id,
        adresse: image.adresse,
        positionX: image.positionX,
        positionY: image.positionY,
        largeur: image.largeur,
        hauteur: image.hauteur,
      })),
    [scene.data],
  );

  /**
   * **Les pense-bêtes de la scène — F-22, Q352, et rien ne les peignait non plus.**
   *
   * Leur couleur et leur position viennent du modèle depuis le 20260829 ; avant, le
   * contrat les recevait à l'écriture et **rien ne les écrivait**, donc une ligne
   * d'avant revient sans couleur, à la suite du contenu.
   */
  const penseBetesEnPlace: readonly PenseBeteDeTableau[] = useMemo(
    () =>
      (scene.data?.penseBetes ?? []).map((note) => ({
        id: note.id,
        texte: note.texte,
        couleur: note.couleur,
        positionX: note.positionX,
        positionY: note.positionY,
      })),
    [scene.data],
  );

  /**
   * **Les curseurs nommés des autres, tels que le moteur les peint — DD-08.**
   *
   * « Le curseur porte **le prénom et une couleur propre à la personne pour la durée du
   * cours**, et rien de plus. » La couleur venait d'une dérivation écrite à la main ; le
   * moteur en attribue une par collaborateur, ce qui la rend **stable pendant le cours
   * sans rien conserver** — exactement ce que DD-08 demande, et une fonction de moins à
   * maintenir.
   */
  /**
   * **Le rôle DANS CE COURS, jamais celui du compte** — leçon de D-111, la même que le
   * panneau de visio applique. La réponse de l'entrée le donne.
   */
  const estLeTuteurDuCours =
    entree.data?.participants.some(
      (personne) =>
        personne.personneId === session.data?.compte.id &&
        personne.role === 'tuteur',
    ) ?? false;

  /** Les élèves en relation qui ne sont pas déjà dans ce cours. */
  const elevesAjoutables = useMemo(() => {
    const dejaLa = new Set(
      (entree.data?.participants ?? []).map((p) => p.personneId),
    );
    const vus = new Set<string>();
    return (annuaire.data?.lignes ?? [])
      .flatMap((ligne) => ligne.correspondants)
      .filter((personne) => {
        if (dejaLa.has(personne.id) || vus.has(personne.id)) return false;
        vus.add(personne.id);
        return true;
      });
  }, [annuaire.data, entree.data]);

  const curseurs: readonly CurseurNomme[] = useMemo(
    () =>
      [...canal.etat.curseurs.values()].map((c) => ({
        personneId: c.auteurId,
        prenom: c.prenom,
        x: c.x,
        y: c.y,
      })),
    [canal.etat.curseurs],
  );

  if (coursId === undefined) return null;

  return (
    <div className="flex h-dvh flex-col gap-3 bg-tableau-travail p-3">
      {/* ── L'en-tête, et il tient sur une ligne : la densité est presque nulle ── */}
      <header className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-4">
          <Titre niveau={1} taille="18">
            Cours
          </Titre>
          <Jauge pourcentage={pourcentage ?? 0} gele={gele} />
          {/* ═══════════════════════════════════════════════════════════════
              F-47 — LE RANG DU TABLEAU COURANT, LE NOMBRE DE TABLEAUX DU
              COURS, ET LA CIRCULATION DE L'UN À L'AUTRE.
              ═══════════════════════════════════════════════════════════════

              « **La surface montre le rang du tableau courant et le nombre de
              tableaux du cours**, et on circule de l'un à l'autre » (F-47). « On
              circule librement entre les tableaux d'un cours, **pendant et après**,
              les précédents en **lecture seule** » (Q378).

              **Le nombre est nécessaire, et pas décoratif** : sans lui, « la
              circulation se ferait à l'aveugle, en essayant un rang de plus jusqu'à un
              refus ». Il compte **les tableaux effacés**, leur ligne survivant pour son
              rang.

              **La densité est presque nulle** : deux flèches et un compte, dans
              l'en-tête, à côté de la jauge. Rien de plus ne tient sur cette ligne. */}
          {circulation.rang !== null && nombreDeTableaux !== null && nombreDeTableaux > 1 && (
            <span className="flex items-center gap-2">
              <Bouton
                variante="fantome"
                taille="petite"
                disabled={circulation.rang <= 1}
                aria-label="Tableau précédent"
                onClick={() => void circulation.circuler(circulation.rang! - 1)}
              >
                ‹
              </Bouton>
              <span className="text-14 text-texte-faible">
                Tableau {circulation.rang} sur {nombreDeTableaux}
              </span>
              <Bouton
                variante="fantome"
                taille="petite"
                disabled={circulation.rang >= nombreDeTableaux}
                aria-label="Tableau suivant"
                onClick={() => void circulation.circuler(circulation.rang! + 1)}
              >
                ›
              </Bouton>
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          {/* ═══════════════════════════════════════════════════════════════════
              F-25 — FABRIQUER LE DOCUMENT, ET IL N'Y A RIEN À CHOISIR.
              ═══════════════════════════════════════════════════════════════════

              « **Tout part, toujours** : toutes les cases non vides, **aucun choix
              possible** » (Q371). Ce bouton n'ouvre donc aucune boîte de dialogue, ne
              propose ni intervalle de pages ni format : un seul clic, et le document
              part entier.

              **Il vit dans l'en-tête et non dans la barre d'outils**, parce que la
              barre porte ce qui agit **sur le tracé** — armer un outil, choisir une
              encre, annuler. Le document n'agit sur rien : il sort du tableau, comme
              « Quitter » et « Terminer le cours » sortent du cours.

              **Il attend que le moteur ait remonté ses commandes** : sans elles, il
              n'y a personne pour dessiner les cases. C'est le même `commandes` que les
              trois gestes ponctuels de la barre d'outils, et il arrive au montage. */}
          {/* ═══════════════════════════════════════════════════════════════
              F-24 — LE BOUTON D'IMPORT, PREMIER DES SEPT CHEMINS D'ENTRÉE.
              ═══════════════════════════════════════════════════════════════

              Les trois autres chemins servis n'ont **aucun bouton** et c'est leur
              nature : le collage se fait au clavier, le glisser-déposer sur la surface,
              et la photo passe par le même sélecteur avec la caméra de l'appareil.

              **Il disparaît en relecture et au plafond**, comme le dépôt lui-même. */}
          {!gele && !circulation.enRelecture && tableauVivantId !== null && (
            <ChoixDeFichiers
              libelle="Déposer une image"
              aide="Une image, ou un document PDF."
              onChoisir={(fichiers) =>
                depot.deposerDesFichiers(fichiers, 'import')
              }
            />
          )}
          <Bouton
            variante="douce"
            taille="petite"
            enCours={documentDuTableau.isPending}
            disabled={commandes === null}
            onClick={() => {
              if (commandes === null) return;
              documentDuTableau.mutate(commandes.exporterLesPages);
            }}
          >
            Fabriquer le document
          </Bouton>
          {/* **QUITTER SORT DU COURS, ET IL NE LE FAISAIT PAS** — F-15, D-118.
              Ce bouton ne faisait que naviguer : le serveur continuait de croire la
              personne présente, sa ligne de relevé restait ouverte, et **elle ne
              pouvait entrer dans aucun autre cours** (Q260) jusqu'à ce que le
              balayage de coupure la sorte — en écrivant la mauvaise cause.

              **On navigue dans tous les cas, y compris sur un refus.** Le geste est
              idempotent côté serveur, et retenir quelqu'un sur l'écran qu'il quitte
              parce que le serveur n'a pas répondu serait pire que la sortie tardive
              qu'on corrige. */}
          <Bouton
            variante="douce"
            taille="petite"
            enCours={sortie.isPending}
            onClick={() => {
              void sortie
                .mutateAsync()
                .catch(() => undefined)
                .finally(() => void navigate('/arrivee'));
            }}
          >
            Quitter
          </Bouton>
          {/* **Terminer est réservé au tuteur**, F-17 : c'est un des huit motifs, et
              le seul qui soit un geste humain du tuteur. Le serveur refuse de toute
              façon ; ce test-ci évite de montrer un bouton qui ne marchera pas. */}
          {session.data?.compte.role === 'TUTOR' && (
            <Bouton
              taille="petite"
              enCours={fin.isPending}
              onClick={() =>
                void fin
                  .mutateAsync()
                  // **La navigation n'a lieu que si le serveur a bien terminé le
                  // cours** : y aller quand même montrerait l'écran d'arrivée sur un
                  // cours toujours ouvert. Un refus reste sur place, et `fin.isError`
                  // le dit.
                  .then(() => navigate('/arrivee'))
              }
            >
              Terminer le cours
            </Bouton>
          )}
        </span>
      </header>

      {/* ═════════════════════════════════════════════════════════════════════
          F-16 — AJOUTER UN ÉLÈVE À UN COURS COMMENCÉ, LE TUTEUR DU COURS ET LUI
          SEUL.
          ═════════════════════════════════════════════════════════════════════

          **Le test de rôle porte sur le rôle DANS CE COURS, jamais sur celui du
          compte** — c'est la leçon de D-111 reprise du panneau de visio : un tuteur
          qui n'est pas le tuteur de ce cours-ci verrait sinon un geste menant à un
          `403 AJOUT_RESERVE_AU_TUTEUR`.

          **Replié par défaut, et dans le FLUX quand il s'ouvre.** `DESIGN.md` veut
          une densité « minimale, presque nulle » sur cette surface, et l'ajout est un
          geste rare ; le poser en survol par-dessus le tableau referait D-105 et
          D-113, où un bloc juste dans l'arbre du document recouvrait deux gestes de
          l'en-tête. */}
      {estLeTuteurDuCours && (
        <div className="flex flex-col gap-2">
          {/* **`items-end` et non `items-center`** : la sélection porte son libellé
              au-dessus, donc elle est plus haute que le bouton. Centrés, les deux ne
              s'alignaient sur rien — constaté à la capture d'écran, ce qu'aucun
              contrôle de ce dépôt ne regarde. */}
          <span className="flex items-end gap-2">
            <Bouton
              variante="douce"
              taille="petite"
              onClick={() => poserLAjoutOuvert((ouvert) => !ouvert)}
            >
              {ajoutOuvert ? 'Annuler l’ajout' : 'Ajouter un élève'}
            </Bouton>
            {ajoutOuvert && (
              <>
                {/* **Les trois états de la donnée serveur**, comme `web/CLAUDE.md`
                    l'exige — et l'état vide a ici un sens propre : le tuteur n'a
                    aucun élève en relation qui ne soit déjà dans le cours, et
                    « l'ajout NE CRÉE PAS la relation au passage » (Q194). */}
                {annuaire.isPending && <Squelette lignes={1} />}
                {annuaire.isError && (
                  <EtatErreur
                    message="La liste de vos élèves n’a pas pu être lue."
                    onReessayer={() => void annuaire.refetch()}
                  />
                )}
                {annuaire.isSuccess && elevesAjoutables.length === 0 && (
                  <Bandeau ton="accent">
                    Aucun élève à ajouter : ceux avec qui vous êtes en relation
                    sont déjà dans ce cours. Une mise en relation ne se fait pas
                    ici.
                  </Bandeau>
                )}
                {annuaire.isSuccess && elevesAjoutables.length > 0 && (
                  <>
                    <Selection
                      libelle="Élève à ajouter"
                      value={eleveChoisi}
                      onChange={(evenement) =>
                        poserLEleveChoisi(evenement.target.value)
                      }
                      options={elevesAjoutables.map((eleve) => ({
                        valeur: eleve.id,
                        libelle: nomAffiche(eleve.prenom),
                      }))}
                    />
                    <Bouton
                      taille="petite"
                      enCours={ajout.isPending}
                      onClick={() => {
                        const cible = eleveChoisi || elevesAjoutables[0]?.id;
                        if (cible === undefined) return;
                        void ajout.mutateAsync(cible).then(() => {
                          poserLAjoutOuvert(false);
                          poserLEleveChoisi('');
                          // **On relit la liste des participants en RÉ-ENTRANT**, et
                          // c'est le seul chemin que le contrat offre : la liste
                          // vient de la réponse de l'entrée, qui est une mutation.
                          // **C'est D-114 qui rend ce geste sûr** — depuis sa garde,
                          // rentrer sans être sorti ne rouvre aucune ligne de relevé.
                          entree.mutate();
                        });
                      }}
                    >
                      Ajouter
                    </Bouton>
                  </>
                )}
              </>
            )}
          </span>
          {/* **Les deux refus nommés de F-16 se disent tels quels**, plus le
              troisième que le test de rôle ci-dessus rend inatteignable. */}
          {ajout.isError && (
            <Bandeau ton="echec">{lireLErreur(ajout.error).message}</Bandeau>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════
          F-24 — LES DEUX DERNIERS CHEMINS D'ENTRÉE SERVIS, ET AUCUN N'A DE
          BOUTON DANS L'EN-TÊTE.
          ═════════════════════════════════════════════════════════════════════

          « **Sept chemins d'entrée d'une image** » (Q356). Quatre n'ont besoin de
          rien ici — le bouton d'import est dans l'en-tête, le collage se fait au
          clavier, le glisser-déposer sur la surface, la photo par le même sélecteur.
          **Les deux qui suivent demandent qu'on désigne quelque chose**, un fichier
          ou une adresse, donc ils demandent une surface.

          **Repliée par défaut, et dans le FLUX quand elle s'ouvre** — même arbitrage
          que l'ajout d'un élève juste au-dessus : `DESIGN.md` veut une densité
          « minimale, presque nulle » sur cette surface, et poser ce bloc en survol
          par-dessus le tableau referait D-105 et D-113.

          **Elle disparaît en relecture et au plafond**, comme le dépôt lui-même : une
          scène passée « est figée, personne ne l'écrit » (Q317), et au plafond « tout
          ajout est refusé pour tous les présents » (F-21). */}
      {!gele && !circulation.enRelecture && tableauVivantId !== null && (
        <div className="flex flex-col gap-2">
          <span className="flex items-end gap-2">
            <Bouton
              variante="douce"
              taille="petite"
              onClick={() => poserLaPriseOuverte((ouverte) => !ouverte)}
            >
              {priseOuverte ? 'Fermer' : 'Prendre une image ailleurs'}
            </Bouton>
          </span>
          {priseOuverte && (
            <div className="flex flex-col gap-3">
              {/* **Le cinquième chemin — le fichier pris dans son espace.** Le
                  composant n'est monté qu'ici, donc l'espace n'est lu qu'à
                  l'ouverture du geste : c'est une requête de plus sur l'écran dont
                  « la latence du trait est le critère numéro un », et le geste est
                  rare. Même arbitrage que l'annuaire de F-16. */}
              <PriseDansSonEspace
                enCours={depot.enCours}
                onPrendre={depot.deposerUnFichierDeSonEspace}
              />
              {/* ═══════════════════════════════════════════════════════════════
                  **LE SIXIÈME CHEMIN — L'ADRESSE COLLÉE, ET C'EST LE NAVIGATEUR
                  QUI VA CHERCHER.**
                  ═══════════════════════════════════════════════════════════════

                  Q360 : « le navigateur de la personne va chercher l'image à
                  l'adresse collée, **jamais le produit** ». L'adresse ne part donc
                  vers aucune route : elle est donnée au port de contenu, qui la lit
                  dans cet onglet. **Sur échec, rien n'est posé et le refus se dit**
                  (Q361) — il paraît dans le bandeau de refus commun, plus bas.

                  **Le corpus dit ce chemin marginal** (Q360) : « coller une image
                  copiée depuis une page web passe par le **collage** ». L'aide le
                  dit à la personne plutôt que de la laisser buter dessus. */}
              <span className="flex max-w-formulaire items-end gap-2">
                <Saisie
                  libelle="Adresse d’une image"
                  aide="Votre navigateur va la chercher. Beaucoup de pages le refusent : dans ce cas, copiez l’image et collez-la sur le tableau."
                  type="url"
                  inputMode="url"
                  placeholder="https://…"
                  value={adresseCollee}
                  onChange={(evenement) =>
                    poserLAdresseCollee(evenement.target.value)
                  }
                />
                <Bouton
                  taille="petite"
                  enCours={depot.enCours}
                  disabled={adresseCollee.trim() === ''}
                  onClick={() => {
                    depot.deposerUneAdresseWeb(adresseCollee.trim());
                    poserLAdresseCollee('');
                  }}
                >
                  Poser
                </Bouton>
              </span>
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════
          CE QUE LE DOCUMENT A DONNÉ — F-25, ET LES QUATRE SORTIES SE DISENT
          DIFFÉREMMENT.
          ═════════════════════════════════════════════════════════════════════

          **La troisième n'est pas un échec, et l'écrire en rouge serait faux** : « un
          document fabriqué alors que l'espace est plein est **téléchargé quand même**
          et n'est pas rangé, **et le produit le dit** » (F-34 critère 3). Le geste a
          réussi ; c'est le rangement qui n'a pas eu lieu, et la raison est nommée pour
          que la personne sache quoi faire — « le gel se lève en retirant, le produit ne
          détruit jamais de lui-même ». */}
      {/* ═════════════════════════════════════════════════════════════════════
          F-20 CRITÈRE 4 — LE BANDEAU D'OBSERVATION, ET CE QU'IL NE DIT PAS.
          ═════════════════════════════════════════════════════════════════════

          « **Le passage d'un `orgadmin` est signalé par un bandeau**, qui **ne le
          nomme pas** et **ne dit pas combien de personnes regardent** » (Q213, Q226,
          Q426, Q429). « **Celui du compte qui voit tout n'est pas signalé** » (Q214) —
          invisible sur le moment, constatable après coup par sa trace.

          **Ce que le canal porte est un booléen**, et c'est la borne : il n'y a nulle
          part où un nom ou un nombre pourrait se glisser. Q226 en donne le motif — « un
          nom n'ouvre aucune action à qui le lit : il ne peut ni refuser l'observation
          ni s'y opposer, donc il informerait sans servir ».

          **Le ton est `accent` et non `attention`** : ce n'est ni une alerte ni un
          incident, c'est un fait du cours. */}
      {canal.etat.observee && (
        <Bandeau ton="accent">
          Une personne de la structure regarde ce cours.
        </Bandeau>
      )}

      {/* **LA RELECTURE SE DIT, ET ELLE DIT LE GESTE QUI EN SORT.** « Les précédents
          en lecture seule » (Q378) : sans ce bandeau, une personne qui a circulé
          verrait un tableau où son stylet ne pose rien, **sans savoir pourquoi** — et
          c'est exactement ce que `CDC-0` §8 refuse, « le produit explique ses refus,
          partout ». */}
      {circulation.enRelecture && (
        <Bandeau
          ton="accent"
          action={
            <Bouton
              variante="douce"
              taille="petite"
              onClick={circulation.revenirAuVivant}
            >
              Revenir au tableau en cours
            </Bouton>
          }
        >
          Vous regardez un tableau précédent de ce cours. Il est en lecture seule.
        </Bandeau>
      )}

      {/* **LE REFUS D'UN DÉPÔT PORTE CE QUE LE CORPUS EXIGE DE LUI** — la place
          restante et le besoin (Q367), ou le geste qui lève le refus (DD-19). La
          phrase est composée par la feature ; cet écran la montre. */}
      {depot.refus !== null && (
        <Bandeau
          ton="echec"
          action={
            <Bouton
              variante="douce"
              taille="petite"
              onClick={depot.oublierLeRefus}
            >
              J’ai compris
            </Bouton>
          }
        >
          {depot.refus}
        </Bandeau>
      )}

      {documentDuTableau.isError && (
        <Bandeau ton="echec">
          {lireLErreur(documentDuTableau.error).message}
        </Bandeau>
      )}

      {documentDuTableau.data !== undefined &&
        (documentDuTableau.data.pages === 0 ? (
          // « Les cases vides sont écartées : le document n'a jamais de page blanche »
          // (Q341). Un tableau où rien n'est écrit n'a donc **aucune** page, et un
          // document sans page n'est pas un document : rien n'a été demandé au serveur.
          <Bandeau ton="attention">
            Ce tableau ne porte encore rien : il n’y a aucune page à mettre dans
            le document.
          </Bandeau>
        ) : documentDuTableau.data.range ? (
          <Bandeau ton="positif">
            Document fabriqué en {documentDuTableau.data.pages} page
            {documentDuTableau.data.pages > 1 ? 's' : ''} : il est téléchargé et
            rangé dans votre espace de fichiers.
          </Bandeau>
        ) : documentDuTableau.data.raison === 'PLAFOND_ESPACE_ATTEINT' ? (
          <Bandeau ton="attention">
            Document téléchargé, mais pas rangé : votre espace de fichiers est
            plein. Retirez-y quelque chose pour y ranger les suivants.
          </Bandeau>
        ) : documentDuTableau.data.raison === 'NOM_DEJA_PRIS' ? (
          // « Deux fabrications du même cours se distinguent par l'instant, et deux
          // fabrications DANS LA MÊME SECONDE se heurtent — le produit ne renomme pas
          // et n'écrase pas. » Le geste qui lève le refus est donc d'attendre.
          <Bandeau ton="attention">
            Document téléchargé, mais pas rangé : un document du même instant est
            déjà dans votre espace. Refaites le geste dans une seconde.
          </Bandeau>
        ) : (
          // Un `orgadmin` et le compte qui voit tout : « ils le reçoivent en
          // téléchargement seul, et leur droit de fabriquer ne disparaît pas » (Q450).
          <Bandeau ton="positif">
            Document fabriqué en {documentDuTableau.data.pages} page
            {documentDuTableau.data.pages > 1 ? 's' : ''} et téléchargé.
          </Bandeau>
        ))}

      {/* ── Ce que le produit dit de SA PROPRE liaison, et de rien d'autre ── */}
      {canal.etat.coupeDepuisSecondes !== null && (
        <Bandeau ton="attention">
          Vous êtes coupé depuis {canal.etat.coupeDepuisSecondes} secondes. Vous
          pouvez continuer à tracer, mais ce que vous tracez maintenant peut ne
          pas être retenu.
        </Bandeau>
      )}

      {canal.etat.versionRefusee && (
        <Bandeau ton="echec">
          Votre coupure a duré trop longtemps et d’autres ont tracé pendant ce
          temps : ce que vous avez tracé pendant la coupure n’a pas été retenu.
        </Bandeau>
      )}

      {/* ═════════════════════════════════════════════════════════════════════
          CE QUE LE PRODUIT DIT DE SA PROPRE VISIO, ET DE CELLE DE PERSONNE
          D'AUTRE — DD-07, DD-18.
          ═════════════════════════════════════════════════════════════════════

          Les quatre bandeaux ci-dessous portent tous sur SON poste. « Le produit ne
          montre pas la qualité de liaison des autres : aucun voyant sur qui que ce
          soit », et « les autres voient qu'elle n'a plus ni image ni son, SANS SAVOIR
          POURQUOI ». L'asymétrie est la même que celle du tableau, et pour le même
          motif : « sur sa propre coupure une personne peut agir ». */}

      {visio.etat.visioDisponible === false && (
        // **Ce n'est pas une erreur, c'est un mode de marche** (DD-18) : « si le
        // service extérieur est indisponible au démarrage, le cours démarre quand
        // même, sans visio ». Le ton est donc neutre et rien n'est proposé — il n'y a
        // aucun geste qui la rétablirait, et en offrir un mentirait.
        <Bandeau ton="accent">
          Ce cours n’a pas de visio. Le cours continue sur le tableau, et le
          tableau n’en dépend pas.
        </Bandeau>
      )}

      {visio.etat.demarrageEchoue && (
        // **DD-18, et ce n'est PAS la phrase de la chute** : « si le service extérieur
        // est indisponible au démarrage, le cours démarre quand même, sans visio ».
        // Rien n'est tombé, donc on ne dit pas « vous êtes tombé » — c'était le défaut
        // trouvé au navigateur le 20260827. Le produit « ne désigne aucune cause », et
        // il retente tout seul tant que le cours vit.
        <Bandeau ton="attention">
          La visio n’a pas démarré. Le produit retente, et le cours continue sur
          le tableau.
        </Bandeau>
      )}

      {visio.etat.coupeDepuisSecondes !== null && (
        <Bandeau ton="attention">
          {visio.etat.coupeDepuisSecondes >= SEUIL_DE_MESSAGE_S
            ? // « Au bout de trente secondes, il lui dit que le cours continue sur le
              // tableau » (DD-18). Le produit « ne désigne aucune cause », et il
              // « tente de rétablir tout seul, tant que le cours vit ».
              'Votre image et votre son sont tombés. Le produit retente, et le cours continue sur le tableau.'
            : 'Votre image et votre son sont tombés. Le produit retente.'}
        </Bandeau>
      )}

      {!visio.etat.monImageEstDiffusee && (
        // Q417 : « le produit dit à la personne que son image n'est plus diffusée et
        // qu'elle le sera quand elle parlera ». **La bascule est automatique et
        // réversible**, donc ce bandeau part de lui-même.
        <Bandeau ton="attention">
          Il y a plus de six images dans ce cours : la vôtre n’est plus diffusée
          pour l’instant. Elle le sera de nouveau quand vous parlerez. Vous
          restez présent et audible.
        </Bandeau>
      )}

      {visio.etat.materielRefuse && (
        // « Une caméra ou un micro absents, en panne, ou refusés par le navigateur
        // n'empêchent jamais d'entrer » (DD-15) : on est entré, donc ce n'est pas un
        // échec — c'est un état à dire.
        <Bandeau ton="attention">
          Votre navigateur n’a pas donné accès à la caméra ou au micro. Vous
          êtes dans le cours, et vous pouvez tracer.
        </Bandeau>
      )}

      {coupure.data?.coupe === false && (
        // **`coupe` faux veut dire que rien n'a été coupé**, et l'écran doit le dire :
        // un tuteur qui croit avoir coupé le micro d'un élève et ne l'a pas coupé est
        // exactement le défaut « un écran qui a l'air de marcher ». Le geste, lui, est
        // tracé — la ligne s'écrit avant l'appel au service extérieur.
        <Bandeau ton="echec">
          Le flux n’a pas été coupé : le service de visio n’a pas répondu. Votre
          geste est enregistré, la personne publie toujours.
        </Bandeau>
      )}

      {fenetre.ouverte && (
        /**
         * **UN BANDEAU DANS LE FLUX, ET PAS UNE FENÊTRE PAR-DESSUS LE TABLEAU.**
         *
         * C'est la leçon de D-105 et de D-113, que D-118 a déjà appliquée à l'ajout d'un
         * élève : « le poser en survol par-dessus le tableau referait exactement D-105 et
         * D-113 ». Une boîte modale sur cette surface recouvrirait le tracé — et **aucun
         * contrôle de ce dépôt ne voit un recouvrement**. Le bandeau prend sa place, il
         * la prend pour tout le monde en même temps, et il la rend dès qu'on répond.
         *
         * **N'IMPORTE QUEL PRÉSENT RÉPOND, ET IL RÉPOND POUR TOUT LE COURS.** Aucune
         * condition de rôle : la fenêtre « regarde le cours, pas les personnes » (Q290).
         * Le texte dit ce que le geste fait vraiment — il repousse la fenêtre pour tout
         * le monde — plutôt que de laisser croire qu'il sauve son auteur seul, ce que la
         * déconnexion individuelle du critère 2 ferait si elle était servie, et **elle ne
         * l'est pas** (D-120).
         *
         * **Le compte à rebours ne se montre pas comme une menace précise.** Ce que le
         * produit sait est l'échéance du cours, pas ce qui arrive à chaque personne :
         * annoncer « vous serez déconnecté dans 4:12 » serait affirmer un mécanisme qui
         * n'existe pas.
         */
        <Bandeau
          ton="attention"
          action={
            <Bouton
              taille="petite"
              enCours={reponseALaFenetre.isPending}
              onClick={() => {
                reponseALaFenetre.mutate();
              }}
            >
              Nous sommes là
            </Bouton>
          }
        >
          Rien ne s’est passé sur ce cours depuis un moment, et il va se fermer
          {fenetre.resteSecondes > 0
            ? ` dans ${minutesEtSecondes(fenetre.resteSecondes)}`
            : ' d’un instant à l’autre'}
          . Répondez pour le garder ouvert — une seule réponse suffit pour tout
          le monde.
        </Bandeau>
      )}

      {reponseALaFenetre.isError && (
        <EtatErreur message={lireLErreur(reponseALaFenetre.error).message} />
      )}

      {gele && (
        // « Au plafond, le produit **propose d'ouvrir un tableau neuf**, et n'importe
        // quel présent l'ouvre — le premier qui accepte débloque le cours pour tout le
        // monde. » Aucune condition de rôle, et c'est la règle.
        <Bandeau
          ton="echec"
          action={
            <Bouton
              taille="petite"
              enCours={tableau.isPending}
              onClick={() => tableau.mutate()}
            >
              Ouvrir un tableau neuf
            </Bouton>
          }
        >
          Ce tableau est plein. Personne ne peut plus y écrire, pas même le
          tuteur. Ouvrez-en un neuf, ou retirez du contenu.
        </Bandeau>
      )}

      {ecriture.isError && (
        <EtatErreur message={lireLErreur(ecriture.error).message} />
      )}

      {tableau.isPending && <Squelette lignes={2} />}

      {tableau.isError && (
        <EtatErreur
          message={lireLErreur(tableau.error).message}
          onReessayer={() => tableau.mutate()}
        />
      )}

      {/* **La surface paraît dès qu'on SAIT où écrire, et non dès qu'on a ouvert un
          tableau — D-110.** La garde lisait `tableau.data`, donc elle n'était vraie que
          si cette page avait elle-même ouvert un tableau : avec le correctif, un poste
          qui rejoint un tableau existant n'en ouvre plus, et cette garde aurait fait
          **disparaître le tableau entier**. C'est le même défaut d'un cran plus loin —
          une valeur employée comme preuve d'un état qu'elle ne mesure pas. */}
      {/* ═════════════════════════════════════════════════════════════════════
          LA VISIO EST UNE COLONNE À DROITE, DANS LE FLUX — ET ELLE A D'ABORD
          ÉTÉ POSÉE EN SURVOL, CE QUI RECOUVRAIT DEUX GESTES DU TUTEUR.
          ═════════════════════════════════════════════════════════════════════

          **Constaté à la capture d'écran le 20260827, et c'est D-105 mot pour mot** :
          « un motif juste dans l'arbre du document et faux à l'écran ». Le panneau
          flottait en haut à droite, au motif que « tout ce qui n'est pas le tracé prend
          de la place sur le tracé » (`DESIGN.md`) — et il se posait **exactement sur
          « Quitter » et « Terminer le cours »**, qui vivent au même coin de l'en-tête.
          Le tuteur ne pouvait plus terminer son cours.

          **Et la sonde ne l'a pas dit** : elle vérifiait que le panneau était
          entièrement dans la fenêtre, ce qui était vrai. Un bloc peut être dans la
          fenêtre et par-dessus un bouton — **il a fallu regarder l'image**.

          **Ce que la colonne dans le flux coûte, et c'est écrit plutôt que caché** : le
          tableau perd un sixième de sa largeur quand le cours a un canal. La
          pré-maquette n'avait pas ce coût parce qu'elle rangeait « Terminer le cours »
          et « Ajouter un participant » **dans cette même colonne**, sous les vignettes ;
          les remonter là serait la façon de récupérer la largeur, et c'est un
          déplacement de gestes du tuteur — donc pas un geste de ce lot. */}
      <div className="flex min-h-0 flex-1 gap-3">
        {/* **LE COLLAGE ET LE GLISSER-DÉPOSER VIVENT SUR LA COLONNE DU TABLEAU** —
            deux des sept chemins d'entrée de F-24 (Q356), et les deux seuls qui n'ont
            aucun bouton : ils se font là où l'on regarde.

            **`onPaste` demande que le bloc puisse recevoir le focus** — sans
            `tabIndex`, l'événement de collage ne lui parvient jamais. `outline-none`
            n'est pas posé : `:focus-visible` reste visible, c'est la règle de la
            charte. */}
        <div
          className="flex min-h-0 flex-1 flex-col gap-3"
          tabIndex={-1}
          onPaste={depot.surCollage}
          onDragOver={depot.surSurvol}
          onDrop={depot.surDepose}
        >
          {tableauVivantId !== null && (
            <>
              <BarreDOutils
                outil={outil}
                encre={encre}
                epaisseur={epaisseur}
                gele={gele || circulation.enRelecture}
                onOutil={poserOutil}
                onEncre={poserEncre}
                onEpaisseur={poserEpaisseur}
                onAnnuler={() => commandes?.annuler()}
                onRefaire={() => commandes?.refaire()}
                onToutEffacer={() => {
                  // « N'importe quel présent exerce le tout effacer » (Q348), et « tout
                  // le monde efface le tracé de tout le monde » (Q350). **Le geste passe
                  // désormais par le moteur, qui rend les identités retirées au serveur**
                  // — avant, il ne vidait qu'un état local, donc il n'effaçait pour
                  // personne et rien n'était conservé.
                  commandes?.toutEffacer();
                  poserPoidsLocal(0);
                }}
              />

              <SurfaceDeTrace
                elementsEnPlace={elementsEnPlace}
                /* ═══════════════════════════════════════════════════════════
                   **`elementsRecus` APPARTIENT AU TABLEAU VIVANT, ET À LUI SEUL.**
                   ═══════════════════════════════════════════════════════════

                   Le canal diffuse ce que les autres posent **sur le tableau où le
                   cours travaille**. Les peindre sur une scène passée y ferait
                   apparaître, en direct, des tracés qui n'y sont pas — et « la
                   scène est **figée** dès l'ouverture du tableau suivant » (Q327).

                   **Le port dédoublonne par identité**, donc rien ne se perd : au
                   retour sur le tableau vivant, tout ce qui est arrivé pendant la
                   relecture est repeint d'un coup. */
                elementsRecus={circulation.enRelecture ? [] : canal.etat.recus}
                imagesEnPlace={imagesEnPlace}
                penseBetesEnPlace={penseBetesEnPlace}
                /* **DD-12 et la règle 14 du domaine 4** : « deux personnes qui
                   relisent la même scène **ne se voient pas** », et une personne
                   partie regarder un tableau précédent « **n'a plus de curseur sur
                   le tableau vivant, et n'en a pas sur celui qu'elle regarde** ».
                   Elle **reste présente** dans la liste des participants — la
                   présence est celle du cours, pas celle d'un tableau. */
                curseurs={circulation.enRelecture ? [] : curseurs}
                outil={outil}
                encre={encre}
                epaisseur={epaisseur}
                gele={gele}
                /* **La lecture stricte n'est PAS le gel** : voir la prop au port.
                   Elle ferme l'écriture ET le pointeur ; le gel ne ferme que
                   l'écriture, et il dit autre chose — « ce tableau est plein ». */
                lectureStricte={circulation.enRelecture}
                onPret={poserLesCommandes}
                onPose={(element, poids) => {
                  poserPoidsLocal((precedent) => precedent + poids);
                  // **Le canal d'abord, la persistance ensuite.** « Tout ce qu'une
                  // personne pose part vers tous les autres présents, **sans filtre et
                  // sans délai voulu** » : attendre la réponse du serveur pour diffuser
                  // ferait passer le trait des autres par un aller-retour de base.
                  if (tableauVivantId === null) return;
                  canal.diffuserLElement(tableauVivantId, element);
                  // **Rien à réconcilier : l'élément part sous son identité de
                  // naissance**, et c'est sous elle que le serveur le connaîtra. La
                  // réponse n'a donc plus à être lue pour que la gomme puisse le
                  // désigner — les refus, eux, sont dits à l'écran par
                  // `ecriture.error`.
                  ecriture.mutate({
                    elements: [element],
                    coupureSecondes: canal.etat.coupeDepuisSecondes,
                  });
                }}
                onMiseAJour={(identite, element) => {
                  // **D-121, remontée servie.** « Déplacer ou redimensionner un
                  // élément déjà annoncé ne se conserve pas, il revient au
                  // rechargement là où il est né. » Le canal reçoit aussi le
                  // changement : la diffusion précède la persistance, comme à la
                  // pose — « le canal d'abord, la persistance ensuite ».
                  if (tableauVivantId === null || circulation.enRelecture) return;
                  canal.diffuserLElement(tableauVivantId, element);
                  miseAJour.mutate({ identite, element });
                }}
                onRetrait={(identites) => {
                  // ═══════════════════════════════════════════════════════════════
                  // **LA GOMME PERSISTE, ET ELLE PERSISTE LE TRACÉ DE TOUT LE MONDE.**
                  // ═══════════════════════════════════════════════════════════════
                  //
                  // « **Tout le monde efface le tracé de tout le monde** » (Q350), et
                  // deux des trois endroits où « un élève détruit le travail d'un adulte
                  // sans recours » passent par cette règle.
                  //
                  // **Ce qui la rendait fausse dès qu'il y avait deux personnes** : la
                  // route de retrait ne connaissait que la clé du serveur, et un poste
                  // ne l'apprend que pour les éléments dont il a lui-même reçu la
                  // réponse d'écriture. Le trait d'un autre disparaissait à l'écran,
                  // restait en base, et revenait au rechargement — ce que D-116 avait
                  // mesuré et refusé de masquer.
                  //
                  // **Ce qui la rend vraie** : l'élément porte son identité de naissance
                  // partout — le canal la transporte avec lui, l'écriture la déclare, et
                  // la route de retrait l'accepte. Il n'y a donc plus rien à traduire, et
                  // **plus aucune table de correspondance dans cette page**.
                  //
                  // Le contrat n'a pas de route de lot : un appel par élément.
                  if (tableauVivantId === null) return;
                  for (const identite of identites) retrait.mutate(identite);
                }}
                onPointeur={(x, y, trace) =>
                  canal.diffuserLePointeur(tableauVivantId, x, y, trace)
                }
              />
            </>
          )}
        </div>

        {entree.isSuccess && visio.etat.visioDisponible === true && (
          <div className="w-1/6 min-w-20 overflow-y-auto">
            <PanneauDeVisio
              vignettes={visio.etat.vignettes}
              sons={visio.etat.sons}
              participants={entree.data?.participants ?? []}
              moiId={session.data?.compte.id ?? null}
              // **F-31 et F-32 réservent le geste de coupure au tuteur DU COURS**, et
              // c'est le rôle DANS CE COURS qui compte, jamais celui du compte : la
              // réponse de l'entrée le donne. Le serveur refuse de toute façon ; ce
              // test-ci évite de montrer un geste qui ne marchera pas — la leçon de
              // D-111, où `hasCreate` faisait paraître un bouton menant à un `403`.
              estTuteur={
                entree.data?.participants.some(
                  (personne) =>
                    personne.personneId === session.data?.compte.id &&
                    personne.role === 'tuteur',
                ) ?? false
              }
              microOuvert={visio.etat.microOuvert}
              cameraAllumee={visio.etat.cameraAllumee}
              ecranPartage={visio.etat.ecranPartage}
              onMicro={() => void visio.basculerLeMicro()}
              onCamera={() => void visio.basculerLaCamera()}
              onEcran={() => void visio.basculerLEcran()}
              onCouper={(personneId, flux) =>
                coupure.mutate({ personneId, flux })
              }
              coupureEnCours={coupure.isPending}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * **Un reste de temps en minutes et secondes, arrondi à la seconde entière.**
 *
 * **Les instants du produit sont tronqués à la seconde entière** (DD-65), et un compte à
 * rebours qui afficherait des dixièmes prétendrait à une précision que rien ne porte.
 */
function minutesEtSecondes(secondes: number): string {
  const minutes = Math.floor(secondes / 60);
  const reste = secondes % 60;
  return `${minutes}:${String(reste).padStart(2, '0')}`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-24, CINQUIÈME CHEMIN — PRENDRE UN FICHIER DANS SON ESPACE, ET LE TABLEAU EN
 * GARDE UNE COPIE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Q356 : « **un fichier pris dans son espace est copié, jamais lié** — un lien ferait
 * dépendre un tableau figé d'un fichier que son propriétaire peut supprimer », et `CDC-3`
 * F-34 le dit de l'autre côté : « un fichier pris pour être posé sur un tableau y est
 * **réduit**, et l'exemplaire de l'espace **reste intact** » (Q455). C'est pourquoi ce
 * composant ne transmet **aucun identifiant de fichier** au tableau : il demande l'adresse
 * signée, et la feature du cours en lit les octets.
 *
 * **`CDC-3` F-37 nomme ce geste dans les droits d'un dossier reçu** — « il ouvre,
 * télécharge, **prend un fichier pour un tableau**, dépose » —, donc les dossiers reçus
 * entrent dans cette liste au même titre que les siens.
 *
 * **LA PAGE COMPOSE DEUX FEATURES, ET C'EST SA RAISON D'ÊTRE.** `DOCTRINE.md` §10, encodé
 * dans `eslint.config.mjs`, pose qu'« une feature n'atteint QUE la sienne » : la feature du
 * cours ne peut pas lire l'espace. Même arbitrage que l'annuaire de F-16 plus haut.
 *
 * **LES TROIS ÉTATS SONT TRAITÉS**, comme `web/CLAUDE.md` l'exige de toute surface qui
 * affiche de la donnée serveur — et l'état vide a ici un sens propre : la personne n'a
 * aucun fichier posable, et le geste qui le comble est **dans son espace**, pas ici.
 */
function PriseDansSonEspace({
  enCours,
  onPrendre,
}: {
  readonly enCours: boolean;
  readonly onPrendre: (adresse: string, nom: string) => void;
}) {
  const espace = useEspace();
  const lien = useOuvrirUnFichier();
  const [fichierChoisi, poserLeFichierChoisi] = useState('');

  /**
   * **Les fichiers posables, à plat, et ce qui est écarté n'est pas une règle nouvelle.**
   *
   * Le tableau ne reçoit que des images et des PDF — F-24 pour les premières, la décision
   * de périmètre du 20260810 pour les seconds —, et la route de dépôt ne connaît que ces
   * deux types. Montrer un fichier que le tableau refusera serait un bouton qui mène à un
   * refus, ce que D-111 a mesuré. **Le tri n'est PAS une liste de formats** : « le type
   * réel est contrôlé par la signature binaire » (Q357), et c'est le serveur qui tranche.
   */
  const posables = useMemo(() => {
    const donnees = espace.data;
    if (donnees === undefined) return [];
    const tous = [
      ...donnees.dossiers.flatMap((dossier) => dossier.fichiers),
      ...donnees.dossiersRecus.flatMap((dossier) => [
        ...dossier.fichiers,
        ...dossier.sousDossiers.flatMap((sous) => sous.fichiers),
      ]),
    ];
    return tous
      .filter(
        (fichier) =>
          fichier.typeReel.startsWith('image/') ||
          fichier.typeReel === 'application/pdf',
      )
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [espace.data]);

  if (espace.isPending) return <Squelette lignes={1} />;
  if (espace.isError) {
    return (
      <EtatErreur
        message="Votre espace de fichiers n’a pas pu être lu."
        onReessayer={() => void espace.refetch()}
      />
    );
  }
  if (posables.length === 0) {
    return (
      <Bandeau ton="accent">
        Aucune image ni aucun PDF dans votre espace de fichiers. Déposez-en un
        depuis « Mon espace », puis revenez le prendre ici.
      </Bandeau>
    );
  }

  return (
    <span className="flex max-w-formulaire items-end gap-2">
      <Selection
        libelle="Fichier de votre espace"
        aide="Le tableau en garde une copie ; l’exemplaire de votre espace ne bouge pas."
        value={fichierChoisi}
        onChange={(evenement) => poserLeFichierChoisi(evenement.target.value)}
        options={posables.map((fichier) => ({
          valeur: fichier.id,
          libelle: fichier.nom,
        }))}
      />
      <Bouton
        taille="petite"
        enCours={enCours || lien.isPending}
        onClick={() => {
          const cible = fichierChoisi || posables[0]?.id;
          if (cible === undefined) return;
          // **L'adresse se demande AU MOMENT DU GESTE**, jamais à l'affichage : elle
          // est « présignée et à durée courte », et en précharger une par fichier en
          // ferait autant d'adresses valides qui traînent dans la mémoire d'un onglet.
          lien.mutate(cible, {
            onSuccess: (obtenu) => onPrendre(obtenu.url, obtenu.nom),
          });
        }}
      >
        Poser sur le tableau
      </Bouton>
    </span>
  );
}
